import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", contact_name: "", company: "", phone: "", siret: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <main data-testid="register-success" className="flex justify-center px-6 pt-12 sm:pt-20 pb-16">
        <div className="paper p-10 max-w-md text-center space-y-4">
          <Clock className="w-10 h-10 mx-auto text-primary" strokeWidth={1.4} />
          <h1 className="text-3xl">Demande enregistrée</h1>
          <p className="text-muted-foreground text-sm">
            Votre compte a été créé et est en <b>attente de validation</b> par l'équipe Atelier Inter-Habitat.
            Vous recevrez une confirmation une fois activé.
          </p>
          <div className="text-xs text-muted-foreground bg-secondary/50 rounded-lg p-4">
            <p><b>Email</b> : {form.email}</p>
            <p><b>Contact</b> : {form.contact_name}</p>
          </div>
          <Button onClick={() => navigate("/login")} data-testid="back-to-login" className="btn-secondary w-full">
            Retour à la connexion
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="register-page" className="flex justify-center px-6 pt-12 sm:pt-20 pb-16">
      <form onSubmit={submit} className="paper p-8 lg:p-10 w-full max-w-md space-y-4">
        <div>
          <p className="eyebrow mb-2">Nouveau compte</p>
          <h1 className="text-3xl">Créer un compte pro</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Chaque inscription est validée manuellement par notre équipe.
          </p>
        </div>
        <div>
          <Label>Nom complet *</Label>
          <Input data-testid="reg-name" required value={form.contact_name} onChange={set("contact_name")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Société</Label>
            <Input data-testid="reg-company" value={form.company} onChange={set("company")} />
          </div>
          <div>
            <Label>SIRET *</Label>
            <Input
              data-testid="reg-siret"
              required
              value={form.siret}
              onChange={set("siret")}
              inputMode="numeric"
              pattern="[0-9 ]{14,17}"
              minLength={14}
              maxLength={17}
              placeholder="14 chiffres"
              title="Le SIRET doit contenir 14 chiffres (espaces autorisés)"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Email *</Label>
            <Input data-testid="reg-email" type="email" required value={form.email} onChange={set("email")} />
          </div>
          <div>
            <Label>Téléphone</Label>
            <Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} />
          </div>
        </div>
        <div>
          <Label>Mot de passe * (6 min.)</Label>
          <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={set("password")} />
        </div>
        <Button data-testid="reg-submit" type="submit" className="btn-primary w-full" disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Envoyer la demande <CheckCircle2 className="w-4 h-4" /></>}
        </Button>
        <p className="text-sm text-muted-foreground text-center">
          Déjà un compte ? <Link to="/login" className="text-primary font-medium hover:underline">Se connecter</Link>
        </p>
      </form>
    </main>
  );
}
